import axios, { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { TextInput } from '../../components/Inputs';
import BodyWrap from '../../components/BodyWrap';
import { Accordion, Select, Textarea } from '@mantine/core';
import { GradientButton } from '../../components/Buttons';
import { useQuery } from '@tanstack/react-query';
import { Datablock, Metadata, StandardPrompt, VariableText } from '../../types/Datablock';
import { IconBinaryTree2Filled } from '@tabler/icons-react';
import { clearLocalStorage } from '../../utils/authUtils';

function DatablockEditor() {
  useEffect(() => {
    document.title = 'Edit datablock | PromptSlate';
  }, []);
  const navigate = useNavigate();
  const { project_key, env_key, datablock_key } = useParams();
  let [selectedDatablockType, setSelectedDatablockType] = useState<string>('');
  let [datablockName, setDatablockName] = useState<string>('');
  let [modelName, setModelName] = useState<string>('');
  let [locale, setLocale] = useState<string>('');
  let [changelog, setChangelog] = useState<string>('');
  let [datablockKey, setDatablockKey] = useState<string>('');
  let [datablockListMessage, setDatablockListMessage] = useState<string>('');
  let [datablockUpdateMessage, setDatablockUpdateMessage] = useState<string>('');
  const [accordionValue, setAccordionValue] = useState<string | null>(null);

  const [standardPromptData, setStandardPromptData] = useState<StandardPrompt | null>(null);
  const [standardPromptRole, setStandardPromptRole] = useState<string>('');
  const [standardPromptText, setStandardPromptText] = useState<string>('');

  const [variableTextData, setVariableTextData] = useState<VariableText | null>(null);
  const [variableData, setVariableData] = useState<string>('');

  const [originalDatablockName, setOriginalDatablockName] = useState('');
  const [originalDatablockData, setOriginalDatablockData] = useState<
    StandardPrompt | VariableText | null
  >(null);
  const [originalDatablockMetadata, setOriginalDatablockMetadata] = useState<Metadata | null>(null);

  function isStandardPrompt(data: any): data is StandardPrompt {
    return typeof data.role === 'string' && typeof data.prompt === 'string';
  }

  function isVariableText(data: any): data is VariableText {
    return typeof data.variable === 'string';
  }

  useEffect(() => {
    if (variableTextData) {
      setVariableData(variableTextData.variable);
    }

    if (standardPromptData) {
      setStandardPromptRole(standardPromptData.role);
      setStandardPromptText(standardPromptData.prompt);
    }
  }, [variableTextData, standardPromptData]);

  const {
    error,
    data: datablockData,
    refetch: refetchDatablockData,
  } = useQuery({
    queryKey: ['datablock'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const authToken = localStorage.getItem('authToken');

        if (authToken) {
          const headers = {
            Authorization: 'Bearer ' + authToken,
          };
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/project/${project_key}/environment/${env_key}/datablock/${datablock_key}`,
            {
              headers: headers,
            }
          );
          const datablock: Datablock = response.data;
          setSelectedDatablockType(datablock.type);
          setDatablockName(datablock.name);
          setDatablockKey(datablock.key);

          if (datablock.metadata) {
            setAccordionValue('metadata');
            setModelName(datablock.metadata.model);
            setLocale(datablock.metadata.locale);
          }

          switch (datablock.type) {
            case 'STANDARD_PROMPT':
              if (isStandardPrompt(datablock.data)) {
                setStandardPromptData(datablock.data);
              } else {
                console.error('Invalid STANDARD_PROMPT data format');
              }
              break;
            case 'VARIABLE_DATA':
              if (isVariableText(datablock.data)) {
                setVariableTextData(datablock.data);
              } else {
                console.error('Invalid VARIABLE_DATA format');
              }
              break;
            default:
              console.error('Unknown datablock type');
          }
          setOriginalDatablockName(datablock.name);
          setOriginalDatablockData(datablock.data);
          setOriginalDatablockMetadata(datablock.metadata);
          return datablock;
        } else {
          return null;
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.status === 401) {
            clearLocalStorage();
            navigate('/login');
          }
          if (axiosError.code === 'ERR_BAD_REQUEST') {
            setDatablockListMessage('Unable to fetch datablock details');
            return null;
          } else {
            setDatablockListMessage('A network error occured. Please try again later.');
          }
        } else {
          setDatablockListMessage('An error occured. Please try again later.');
          return null;
        }
      }
    },
  });

  const handlePublishDatablockSubmit = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };

      let datablockData = {};
      switch (selectedDatablockType) {
        case 'STANDARD_PROMPT':
          datablockData = {
            role: standardPromptRole,
            prompt: standardPromptText,
          };
          break;
        case 'VARIABLE_DATA':
          datablockData = {
            variable: variableData,
          };
      }

      let metadata = {
        model: modelName,
        locale,
      };

      const nameChanged = datablockName !== originalDatablockName;
      const dataChanged = JSON.stringify(datablockData) !== JSON.stringify(originalDatablockData);
      const metadataChanged =
        JSON.stringify(metadata) !== JSON.stringify(originalDatablockMetadata);

      if (nameChanged || dataChanged || metadataChanged) {
        const updateRequestBody = {
          project_key,
          environment_key: env_key,
          ...(nameChanged && { name: datablockName }),
          ...(dataChanged && { data: datablockData }),
          ...(metadataChanged && { metadata: metadata }),
          ...(changelog && { changelog }),
        };

        await axios.post(
          `${process.env.REACT_APP_API_URL}/datablock/${datablockKey}/update`,
          updateRequestBody,
          { headers }
        );
        navigate(`/project/${project_key}`);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        }
        if (axiosError.code === 'ERR_BAD_REQUEST') {
          setDatablockUpdateMessage('Unable to update datablock.');
        } else {
          setDatablockUpdateMessage('A network error occured. Please try again later.');
        }
      } else {
        setDatablockUpdateMessage('An error occured. Please try again later.');
      }
    }
  };

  const renderBuilder = (datablockData: Datablock) => {
    switch (datablockData.type) {
      case 'STANDARD_PROMPT':
        return (
          <div>
            <div className="mt-8">
              <div className="text-lg">Prompt role</div>
              <Select
                placeholder="Select prompt role"
                data={['system', 'user']}
                allowDeselect={false}
                className="w-[350px] mt-5"
                value={standardPromptRole}
                onChange={(value) => {
                  if (value !== null) {
                    setStandardPromptRole(value);
                  }
                }}
              />
            </div>

            <div className="mt-8">
              <div className="text-lg">Prompt</div>
              <Textarea
                placeholder="Enter your prompt"
                className="mt-5 w-full"
                minRows={15}
                autosize
                resize="vertical"
                value={standardPromptText}
                onChange={(e) => setStandardPromptText(e.target.value)}
              />
            </div>
          </div>
        );
      case 'VARIABLE_DATA':
        return (
          <div>
            <div className="text-lg mt-8">Data</div>
            <Textarea
              placeholder="Enter data"
              className="mt-5 w-full"
              minRows={15}
              autosize
              resize="vertical"
              value={variableData}
              onChange={(e) => setVariableData(e.target.value)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <BodyWrap>
      <div className="pb-5">
        {datablockData ? (
          <div>
            <div className="text-xl">
              <NavLink to={`/dashboard`}>Projects </NavLink>/
              <NavLink
                to={`/project/${project_key}`}
                className="border border-[#ff9148] text-[#ff9148] rounded-lg pl-2 pr-2 ml-2 pb-[1px] mr-2 text-md"
              >
                {project_key}
              </NavLink>
              / Edit Datablock / {datablockData.name}
            </div>

            <div className="mt-6 max-w-screen-xl">
              <div className="mt-8">
                <div className="text-lg">Datablock details</div>
                <div className="flex flex-row gap-8">
                  <TextInput
                    placeholder="Datablock name"
                    className="w-[350px] mt-5"
                    value={datablockName}
                    onChange={(e) => setDatablockName(e.target.value)}
                  />{' '}
                  <TextInput
                    placeholder="Datablock unique key"
                    className="w-[350px] mt-5"
                    value={datablockKey}
                    disabled
                  />
                </div>
              </div>
              <div className="mt-8">
                <Accordion
                  variant="contained"
                  className="bg-white"
                  value={accordionValue}
                  onChange={setAccordionValue}
                >
                  <Accordion.Item value="metadata">
                    <Accordion.Control
                      icon={<IconBinaryTree2Filled size={20} className="text-slate-600" />}
                    >
                      Metadata (optional)
                    </Accordion.Control>
                    <Accordion.Panel className="bg-white">
                      <div className="flex flex-row gap-8">
                        <TextInput
                          placeholder="Model name (eg. gpt-4o, claude-3-sonnet)"
                          className="w-[350px] mt-5"
                          value={modelName}
                          onChange={(e) => setModelName(e.target.value)}
                        />
                        <TextInput
                          placeholder="Locale (eg. en-GB)"
                          className="w-[350px] mt-5"
                          value={locale}
                          onChange={(e) => setLocale(e.target.value)}
                        />
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
                <div className="text-lg"></div>
              </div>
              {renderBuilder(datablockData)}
              <TextInput
                placeholder="Change log (optional)"
                className="w-[450px] mt-5"
                value={changelog}
                onChange={(e) => setChangelog(e.target.value)}
              />{' '}
              <GradientButton
                className="mt-8 w-fit float-right"
                type="submit"
                onClick={handlePublishDatablockSubmit}
              >
                Save and publish
              </GradientButton>
            </div>
          </div>
        ) : (
          <div>Loading datablock</div>
        )}
      </div>
    </BodyWrap>
  );
}

export default DatablockEditor;
